import * as Sentry from '@sentry/react';

// Same bound as primeMapView: a hung request must not stall a restore forever.
export const SESSION_RESTORE_TIMEOUT_MS = 8000;

export interface SessionRestorer {
  restore: () => Promise<void>;
  enabled: boolean;
}

export interface RunSessionRestorersOptions {
  isCurrent: () => boolean;
  timeoutMs?: number;
}

const withTimeout = (
  promise: Promise<void>,
  timeoutMs: number,
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(new Error(`session restorer timed out after ${timeoutMs}ms`)),
      timeoutMs,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Runs the enabled restorers in parallel. Never rejects: a failing or hung
 * restorer is reported to Sentry and does not affect the others. A run that is
 * no longer current (another reconnect landed) is silent, since its socket is
 * expected to be gone.
 */
export async function runSessionRestorers(
  restorers: ReadonlyArray<readonly [string, SessionRestorer]>,
  {
    isCurrent,
    timeoutMs = SESSION_RESTORE_TIMEOUT_MS,
  }: RunSessionRestorersOptions,
): Promise<void> {
  await Promise.all(
    restorers
      .filter(([, { enabled }]) => enabled)
      .map(async ([key, { restore }]) => {
        const startedAt = Date.now();
        try {
          await withTimeout(Promise.resolve().then(restore), timeoutMs);
          if (isCurrent()) {
            Sentry.addBreadcrumb({
              category: 'session.restore',
              message: key,
              level: 'info',
              data: { durationMs: Date.now() - startedAt },
            });
          }
        } catch (error) {
          if (isCurrent()) {
            Sentry.captureException(error, {
              tags: { step: 'restoreSessionState', restorerKey: key },
            });
          }
        }
      }),
  );
}
